import React, { useRef, useMemo, useState, useEffect, useCallback } from "react";
import AddTeamModal from "./AddTeamModal";
import { toast } from "react-toastify";
import RemoveTeamMutation from "../season/mutations/RemoveTeam";
import EditTeamModal from "./EditTeamModal";
import { NavLink } from "react-router-dom";
import InitialsAvatar from "../../util/name_initial";
import InviteLinkBox from "../organization/InviteLinkBox";
import { confirmDelete } from "../common/confirmDelete";
import PaginatedFlatTable from "../pagination/PaginatedFlatTable";
import AddExistingSeasonTeamsModal from "../season/AddExistingSeasonTeamsModal";
import { gql, useMutation } from "@apollo/client";
import useHasFeature from "../common/hooks/useHasFeature";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const ADD_EXISTING_SEASON_TEAMS = gql`
    mutation AddExistingSeasonTeams(
        $seasonId: Int!
        $fromSeasonId: Int!
        $teamIds: [Int!]!
        $keepPlayers: Boolean
        $carryPoints: Boolean
        $deactivateSource: Boolean
        $sourceStageId: Int
        $sourceGroupId: Int
        $targetStageId: Int
        $targetGroupId: Int
    ) {
        addExistingSeasonTeams(
            seasonId: $seasonId
            fromSeasonId: $fromSeasonId
            teamIds: $teamIds
            keepPlayers: $keepPlayers
            carryPoints: $carryPoints
            deactivateSource: $deactivateSource
            sourceStageId: $sourceStageId
            sourceGroupId: $sourceGroupId
            targetStageId: $targetStageId
            targetGroupId: $targetGroupId
        ) {
            success
            errors
            addedSeasonTeams {
                id
                team {
                    id
                    name
                    slug
                    logoUrl
                }
            }
        }
    }
`;

const parseOptionalInt = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
};

export default function TeamList({ teams, setTeams, activeSeasonData, division }) {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const addModalBtnRef = useRef(null);
    const editModalBtnRef = useRef(null);
    const addExistingTeamsBtnRef = useRef(null);

    const { hasFeature } = useHasFeature();
    const pro = hasFeature("pro_standing");

    const [removeTeamMutation] = RemoveTeamMutation();
    const [addExistingSeasonTeams] = useMutation(ADD_EXISTING_SEASON_TEAMS);

    const PAGE_SIZE = 25;
    const [page, setPage] = useState(1);

    const totalCount = Array.isArray(teams) ? teams.length : 0;
    const pages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    useEffect(() => {
        if (page > pages) setPage(pages);
        if (page < 1) setPage(1);
    }, [page, pages]);

    const pageStart = useMemo(() => (page - 1) * PAGE_SIZE, [page]);
    const pageEnd = useMemo(() => pageStart + PAGE_SIZE, [pageStart]);

    const ui = useMemo(() => {
        return {
            sectionCard: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            rowMain: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
            name: { fontSize: 14, fontWeight: 900, color: "#14151a" },
            nameLink: { color: "#14151a", textDecoration: "none" },
            muted: { color: "rgba(20,21,26,0.60)", fontWeight: 700, fontSize: 12.5 },
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.22)",
                color: "#14151a",
                fontWeight: 900,
                fontSize: 12,
                whiteSpace: "nowrap",
            },
            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },
            actionBtnEdit: {
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "#14151a",
            },
            avatarImg: {
                width: 44,
                height: 44,
                borderRadius: 12,
                objectFit: "cover",
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                padding: 4,
            },
        };
    }, []);

    const removeTeam = async (seasonId, teamId) => {
        try {
            const res = await removeTeamMutation({
                variables: {
                    seasonId: parseInt(seasonId, 10),
                    teamId: parseInt(teamId, 10),
                },
            });

            if (res.data.removeTeam.success) {
                const updatedTeams = teams.filter((team) => team.teamId !== teamId);
                setTeams(updatedTeams);

                toast.success(t("teamList.removeSuccess"), {
                    autoClose: 3000,
                    toastId: "remove_team_success",
                    position: "top-center",
                    closeOnClick: true,
                });
            } else {
                toast.error(t("teamList.removeFailed"), {
                    autoClose: 3000,
                    toastId: "remove_team_failed",
                    position: "top-center",
                    closeOnClick: true,
                });
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "teamList.removeFailed"), {
                autoClose: 3000,
                toastId: "remove_team_error",
                position: "top-center",
                closeOnClick: true,
            });
        }
    };

    const requestRemoveTeam = (seasonId, team) => {
        confirmDelete({
            title: t("teamList.removeTitle"),
            message:
                `${t("teamList.removeMessageLine1", { teamName: team.teamName })}\n` +
                `${t("teamList.removeMessageLine2")}\n\n` +
                `${t("teamList.removeMessageLine3")}`,
            confirmLabel: t("teamList.removeConfirm"),
            cancelLabel: t("teamList.cancel"),
            danger: true,
            onConfirm: async () => {
                await removeTeam(seasonId, team.teamId);
            },
        });
    };

    const editTeam = (orgId) => {
        const org = teams.find((org) => org.teamId === orgId);
        if (!org) return;
        const nameEl = document.getElementById("edit-org-name");
        const idEl = document.getElementById("edit-org-id");
        if (nameEl) nameEl.value = org.teamName;
        if (idEl) idEl.value = org.teamId;
    };

    const openAddTeamModal = useCallback(() => {
        addModalBtnRef.current?.click();
    }, []);

    const openAddExistingTeamsModal = useCallback(() => {
        if (!pro) {
            toast.info(t("teamList.proOnlyFeature"), {
                autoClose: 5000,
                position: "top-center",
                toastId: "feature_pro_only",
            });
            return;
        }
        addExistingTeamsBtnRef.current?.click();
    }, [pro, t]);

    return (
        <>
            <button
                ref={addModalBtnRef}
                type="button"
                data-toggle="modal"
                data-target="#add-team-modal"
                style={{ display: "none" }}
                aria-hidden="true"
                tabIndex={-1}
            />
            <button
                ref={editModalBtnRef}
                type="button"
                data-toggle="modal"
                data-target="#edit-team-modal"
                style={{ display: "none" }}
                aria-hidden="true"
                tabIndex={-1}
            />

            <div id="kbola-team-list">
                <InviteLinkBox
                    organizationId={division.organization.id}
                    seasonId={activeSeasonData.id}
                />

                <PaginatedFlatTable
                    title={t("teamList.title")}
                    subtitle={
                        <>
                            {t("teamList.title")} {" "}
                            <b>{activeSeasonData?.name || t("teamList.subtitleSeasonFallback")}</b> ·{" "}
                            <span style={{ color: "rgba(20,21,26,0.55)", fontWeight: 800 }}>
                                {t("teamList.total")}: {totalCount}
                            </span>
                        </>
                    }
                    page={page}
                    pages={pages}
                    items={teams || []}
                    loading={false}
                    addActions={[
                        {
                            label: t("teamList.createTeam"),
                            iconClass: "fas fa-plus",
                            onClick: openAddTeamModal,
                        },
                        {
                            label: t("teamList.addExistingTeams"),
                            iconClass: "fas fa-users",
                            onClick: openAddExistingTeamsModal,
                        },
                    ]}
                    onPageChange={(p) => setPage(p)}
                    headers={[
                        t("teamList.headers.team"),
                        t("teamList.headers.players"),
                        t("teamList.headers.actions"),
                    ]}
                    renderRow={(team, idx) => {
                        if (idx < pageStart || idx >= pageEnd) return null;

                        const playersCount = team?.team?.players?.length || 0;
                        const logoSrc = team?.team?.logoUrl ?? null;

                        return (
                            <tr key={team.teamId || idx}>
                                <td>
                                    <div style={ui.rowMain}>
                                        {logoSrc ? (
                                            <img src={logoSrc} alt="Team Logo" style={ui.avatarImg} />
                                        ) : (
                                            <InitialsAvatar
                                                name={team?.teamName}
                                                size={44}
                                                backgroundColor={colors.primary}
                                                textColor={colors.textOnPrimary}
                                            />
                                        )}

                                        <div style={{ minWidth: 0 }}>
                                            <div style={ui.name}>
                                                <NavLink
                                                    to={`team/${team.team.slug}`}
                                                    state={{
                                                        activeSeasonData,
                                                        team,
                                                        division,
                                                        colors
                                                    }}
                                                    style={ui.nameLink}
                                                    className="kbola-team-link"
                                                    title={t("teamList.openTeamDetails")}
                                                >
                                                    {team?.teamName}
                                                </NavLink>
                                            </div>
                                            <div style={ui.muted}>{t("teamList.seasonTeam")}</div>
                                        </div>
                                    </div>
                                </td>

                                <td>
                                    <span style={ui.chip} title={t("teamList.playersLinked")}>
                                        <i className="fas fa-users" />
                                        {playersCount}
                                    </span>
                                </td>

                                <td className="text-right">
                                    <div style={{ display: "inline-flex", gap: 8 }}>
                                        <button
                                            type="button"
                                            style={{ ...ui.actionBtn, ...ui.actionBtnEdit }}
                                            onClick={() => {
                                                editTeam(team.teamId);
                                                editModalBtnRef.current?.click();
                                            }}
                                            title={t("teamList.editTeamName")}
                                        >
                                            <i className="fas fa-edit" />
                                        </button>

                                        <button
                                            type="button"
                                            style={{ ...ui.actionBtn, ...ui.actionBtnDanger }}
                                            onClick={() => requestRemoveTeam(activeSeasonData.id, team)}
                                            title={t("teamList.removeTeamFromSeason")}
                                        >
                                            <i className="fas fa-trash" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    }}
                />

                <style>{`
          #kbola-team-list .kbola-team-link:hover {
            text-decoration: underline;
            text-decoration-color: rgba(0,0,0,0.25);
          }

          #kbola-team-list button:focus,
          #kbola-team-list a:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          }
        `}</style>
            </div>

            <AddTeamModal
                activeSeasonData={activeSeasonData}
                teams={teams}
                setTeams={setTeams}
                modalElem={addModalBtnRef}
            />
            <EditTeamModal teams={teams} setTeams={setTeams} activeSeasonData={activeSeasonData} />

            <button
                ref={addExistingTeamsBtnRef}
                type="button"
                data-toggle="modal"
                data-target="#add-existing-season-teams-modal"
                style={{ display: "none" }}
                aria-hidden="true"
                tabIndex={-1}
            />

            <AddExistingSeasonTeamsModal
                modalId="add-existing-season-teams-modal"
                divisionId={division.id}
                sport={division?.sport?.key || null}
                activeSeasonData={activeSeasonData}
                currentSeasonTeams={teams}
                onConfirm={async ({
                                      fromSeasonId,
                                      teamIds,
                                      keepPlayers,
                                      carryPoints,
                                      deactivateSource,
                                      sourceStageId,
                                      sourceGroupId,
                                      targetStageId,
                                      targetGroupId,
                                  }) => {
                    const existingIds = new Set((teams || []).map((t) => String(t.teamId)));
                    const toAdd = (teamIds || []).filter((id) => !existingIds.has(String(id)));

                    if (!toAdd.length) {
                        toast.info(t("teamList.allSelectedAlreadyAdded"), {
                            autoClose: 2500,
                            position: "top-center",
                            toastId: "teams_already_in_season",
                        });
                        return;
                    }

                    try {
                        const res = await addExistingSeasonTeams({
                            variables: {
                                seasonId: parseInt(activeSeasonData.id, 10),
                                fromSeasonId: parseInt(fromSeasonId, 10),
                                teamIds: toAdd.map((x) => parseInt(x, 10)),
                                keepPlayers: !!keepPlayers || !!deactivateSource,
                                carryPoints: !!carryPoints,
                                deactivateSource: !!deactivateSource,
                                sourceStageId: carryPoints ? parseOptionalInt(sourceStageId) : null,
                                sourceGroupId: carryPoints ? parseOptionalInt(sourceGroupId) : null,
                                targetStageId: parseOptionalInt(targetStageId),
                                targetGroupId: parseOptionalInt(targetGroupId),
                            },
                        });

                        const payload = res?.data?.addExistingSeasonTeams;

                        if (!payload?.success) {
                            const msg =
                                (payload?.errors && payload.errors.length)
                                    ? payload.errors.join("\n")
                                    : t("teamList.addExistingFailed");
                            toast.error(msg, {
                                autoClose: 3500,
                                position: "top-center",
                                toastId: "add_existing_teams_failed",
                            });
                            return;
                        }

                        const added = payload?.addedSeasonTeams || [];

                        const normalized = added.map((st) => ({
                            teamId: st?.team?.id,
                            teamName: st?.team?.name,
                            logo: st?.team?.logoUrl ?? null,
                            team: {
                                id: st?.team?.id,
                                slug: st?.team?.slug,
                                logoUrl: st?.team?.logoUrl ?? null,
                                players: st?.team?.players ?? [],
                            },
                        }));

                        setTeams((prev) => [...(prev || []), ...normalized]);

                        toast.success(
                            keepPlayers
                                ? t("teamList.teamsAndPlayersCopied")
                                : t("teamList.teamsAdded"),
                            {
                                autoClose: 3000,
                                position: "top-center",
                                toastId: "add_existing_teams_success",
                            }
                        );
                    } catch (err) {
                        toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                            autoClose: 3500,
                            position: "top-center",
                            toastId: "add_existing_teams_error",
                        });
                    }
                }}
            />
        </>
    );
}
